import { Link } from 'react-router-dom';
import type { Activity } from '@/lib/types';
import { activityTypeMeta } from '@/pages/home/activityMeta';

interface MyActivityCardProps {
  activity: Activity;
  created?: boolean;
  onDelete?: () => void;
}

export default function MyActivityCard({ activity, created, onDelete }: MyActivityCardProps) {
  const meta = activityTypeMeta[activity.type];
  const isHelp = activity.type === 'HELP_REQUEST' || activity.type === 'HELP_OFFER';

  return (
    <article className="bg-white border border-background-200 rounded-lg p-4 md:p-5 hover:border-background-300 transition-colors">
      <div className="flex items-start gap-3 md:gap-4">
        <span
          className={`w-11 h-11 md:w-12 md:h-12 shrink-0 rounded-lg ${meta.badgeClass} flex items-center justify-center`}
        >
          <i className={`${meta.icon} text-xl`}></i>
        </span>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span
              className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold ${meta.badgeClass}`}
            >
              <span className={`w-1.5 h-1.5 rounded-full ${meta.dotClass}`}></span>
              {meta.label}
            </span>
            {created && (
              <span className="px-2.5 py-0.5 rounded-full bg-accent-100 text-accent-800 text-xs font-semibold">
                Créée par toi
              </span>
            )}
          </div>

          <Link
            to={`/activite/${activity.id}`}
            className="mt-2 block text-base md:text-lg font-semibold text-foreground-950 leading-snug hover:text-primary-600 transition-colors cursor-pointer"
          >
            {activity.title}
          </Link>

          <div className="mt-2 flex items-center gap-4 flex-wrap text-xs text-foreground-500">
            <span className="flex items-center gap-1.5">
              <i className="ri-map-pin-line text-foreground-400"></i>
              {activity.location}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-time-line text-foreground-400"></i>
              {activity.time}
            </span>
            {!isHelp && (
              <span className="flex items-center gap-1.5">
                <i className="ri-team-line text-foreground-400"></i>
                {activity.participants}/{activity.maxParticipants}
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {created && onDelete && (
            <button
              type="button"
              onClick={onDelete}
              aria-label="Supprimer l'activité"
              className="w-9 h-9 flex items-center justify-center rounded-md text-foreground-400 hover:text-primary-600 hover:bg-primary-50 transition-colors cursor-pointer"
            >
              <i className="ri-delete-bin-line text-lg"></i>
            </button>
          )}
          <Link
            to={`/activite/${activity.id}`}
            aria-label="Voir l'activité"
            className="w-9 h-9 flex items-center justify-center rounded-md text-foreground-400 hover:text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-right-line text-lg"></i>
          </Link>
        </div>
      </div>
    </article>
  );
}